import { Box, MenuItem } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolCheckbox, PoolFilterSearchBar } from '@synergy/components-next';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { FilterMenuItem } from '../styled-components/filter-menu-item';
import { SupplierSearchCount } from '../../../models/supplier-search-count';
import { articleSearchResultsSelector, initialArticleSearchResultsSelector } from '../../../store/articles-selectors';
import { useAppSelector } from '../../../../store/store';
import { useFilter } from '../filter-context';

export const FilterSupplierSearchPage = (): ReactElement => {
    const intl = useIntl();
    const { updateFilter, filter } = useFilter();
    const initialArticleSearchResults = useAppSelector(initialArticleSearchResultsSelector);
    const articleSearchResults = useAppSelector(articleSearchResultsSelector);
    const items = useMemo(() => initialArticleSearchResults.suppliersFromSearch ?? [], [initialArticleSearchResults]);

    const [suppliers, setSuppliers] = useState<SupplierSearchCount[]>(items);
    const [search, setSearch] = useState('');

    useEffect(() => {
        setSuppliers(items.filter(supplier => supplier.name.toLowerCase().includes(search.toLowerCase())));
    }, [items, search]);

    const handleSupplierClick = (supplier: SupplierSearchCount): void => {
        let suppliers = filter.get(FilterParams.SupplierIds)?.split(',');
        if (suppliers?.some(s => s === supplier.id.toString())) {
            suppliers = suppliers.filter(s => s !== supplier.id.toString());
            if (suppliers.length === 0) {
                filter.delete(FilterParams.SupplierIds);
            } else {
                filter.set(FilterParams.SupplierIds, suppliers.join(','));
            }
        } else {
            filter.set(FilterParams.SupplierIds, [...(suppliers ?? []), supplier.id].join(','));
        }
        updateFilter(filter);
    };

    return (
        <>
            <Box sx={{ p: theme => theme.spacing(1.5, 1.5, 0.5) }}>
                <PoolFilterSearchBar
                    onChange={setSearch}
                    placeholder={intl.formatMessage({
                        id: 'app.filter.supplier.enterSupplier',
                    })}
                    onKeyDown={e => e.stopPropagation()}
                    value={search}
                />
            </Box>
            {suppliers.length === 0 && (
                <MenuItem disabled={true} sx={{ '&.Mui-disabled': { opacity: 1 } }}>
                    <FormattedMessage id="app.filter.supplier.noResults" />
                </MenuItem>
            )}

            {suppliers.map(supplier => {
                const text = supplier.name ?? '';
                const nameMatches = match(text, search ?? '', { insideWords: true });
                const nameParts = parse(text, nameMatches);

                const isSupplierChecked =
                    filter
                        .get(FilterParams.SupplierIds)
                        ?.split(',')
                        .some(s => s === supplier.id.toString()) ?? false;

                const disabled =
                    filter.get(FilterParams.SupplierIds) === null &&
                    !articleSearchResults.suppliersFromSearch.find(s => s.id === supplier.id);

                return (
                    <FilterMenuItem
                        value={supplier.id}
                        key={supplier.id}
                        disabled={disabled}
                        onClick={() => handleSupplierClick(supplier)}
                    >
                        <PoolCheckbox checked={isSupplierChecked} color="dark" sx={{ p: 0 }} />
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {nameParts.map((namePart, index) => (
                                <Box
                                    key={index}
                                    component="span"
                                    color={namePart.highlight ? 'primary.main' : undefined}
                                >
                                    {namePart.text}
                                </Box>
                            ))}
                        </Box>
                    </FilterMenuItem>
                );
            })}
        </>
    );
};
